'use client';
import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import { Tool, ALL_TOOLS } from '@/lib/toolRegistry';

const STORAGE_KEY = 'dtb_recently_used';
const MAX_RECENT = 8;

export function recordToolVisit(toolId: string) {
  if (typeof window === 'undefined') return;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const ids: string[] = stored ? JSON.parse(stored) : [];
    const filtered = ids.filter((id) => id !== toolId);
    const updated = [toolId, ...filtered].slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

export default function RecentlyUsedCarousel() {
  const [recentTools, setRecentTools] = useState<Tool[]>([]);
  const [mounted, setMounted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const ids: string[] = JSON.parse(stored);
        const tools = ids
          .map((id) => ALL_TOOLS.find((t) => t.id === id))
          .filter(Boolean) as Tool[];
        setRecentTools(tools);
      }
    } catch {
      // ignore
    }
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -240 : 240, behavior: 'smooth' });
  };

  if (!mounted || recentTools.length === 0) return null;

  return (
    <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
        <div className="flex items-center gap-3">
          {/* Label */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-base">🕐</span>
            <span className="text-xs font-semibold text-text-muted whitespace-nowrap hidden sm:block">
              Recently Used
            </span>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200 shrink-0 hidden sm:block" />

          {/* Scroll left button */}
          <button
            onClick={() => scroll('left')}
            className="shrink-0 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            aria-label="Scroll left"
          >
            <svg className="w-3.5 h-3.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Carousel track */}
          <div
            ref={scrollRef}
            className="flex gap-2 overflow-x-auto scrollbar-hide flex-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {recentTools.map((tool) => (
              <Link
                key={tool.id}
                href={tool.route}
                className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 transition-all duration-150 group"
              >
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-sm shrink-0"
                  style={{ backgroundColor: tool.bgColor }}
                >
                  {tool.icon}
                </span>
                <span className="text-xs font-medium text-text-primary group-hover:text-blue-600 whitespace-nowrap transition-colors">
                  {tool.title}
                </span>
              </Link>
            ))}
          </div>

          {/* Scroll right button */}
          <button
            onClick={() => scroll('right')}
            className="shrink-0 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            aria-label="Scroll right"
          >
            <svg className="w-3.5 h-3.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
